import { useState } from 'react'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { Building2, User, KeyRound, Loader2 } from 'lucide-react'
import './auth/Auth.css'

export default function Register() {
    const [searchParams] = useSearchParams()
    // Landing-page role buttons pass ?role=landlord|tenant
    const initialRole = (searchParams.get('role') as 'landlord' | 'tenant') || 'landlord'
    // Landing-page tenant card can also pre-fill the code via ?code=
    const initialCode = searchParams.get('code') || ''

    const [name, setName] = useState('')
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [role, setRole] = useState<'landlord' | 'tenant'>(initialRole)
    const [inviteCode, setInviteCode] = useState(initialCode.toUpperCase())
    const [error, setError] = useState<string | null>(null)
    const [loading, setLoading] = useState(false)
    const navigate = useNavigate()

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setError(null)
        setLoading(true)

        // Navigate directly to role-specific dashboard — bypasses /app redirect
        // so a stale AppContext state can never send a tenant to landlord routes.
        const destination =
            role === 'tenant' ? '/app/tenant/dashboard' : '/app/landlord/dashboard'

        try {
            // 1. Create Supabase Auth user with role + name in metadata.
            //    The handle_new_user DB trigger will create a matching profiles row.
            const { data, error: signUpError } = await supabase.auth.signUp({
                email,
                password,
                options: {
                    data: { role, name },
                },
            })

            if (signUpError) {
                setError(signUpError.message)
                return
            }

            const userId = data.user?.id
            if (!userId) {
                setError('Account creation failed. Please try again.')
                return
            }

            // 2. Upsert profile explicitly so the role is set even if the trigger
            //    hasn't fired yet (race condition safety net).
            await supabase.from('profiles').upsert({
                id: userId,
                email,
                role,
            })

            // 3. If registering as a tenant with an invite code, claim it via RPC.
            //    The RPC atomically sets tenant_links.tenant_user_id AND profiles.role='tenant'.
            if (role === 'tenant' && inviteCode.trim()) {
                const { data: rpcResult, error: rpcError } = await supabase.rpc(
                    'claim_tenant_invite',
                    { invite_code_input: inviteCode.trim() }
                )

                if (rpcError || (rpcResult && !rpcResult.success)) {
                    const msg =
                        rpcResult?.error ||
                        'Account created, but the invite code could not be applied. ' +
                        'You can enter it later from your dashboard.'
                    setError(msg)
                    // Don't block — let the user into their dashboard
                    navigate(destination, { replace: true })
                    return
                }
            }

            navigate(destination, { replace: true })
        } catch (err) {
            console.error('[REGISTER] unexpected error:', err)
            setError('An unexpected error occurred. Please try again.')
        } finally {
            setLoading(false)
        }
    }

    const toggleStyle = (active: boolean) => ({
        flex: 1,
        padding: '0.5rem 0.75rem',
        border: 'none',
        background: active ? 'var(--color-bg-primary, #fff)' : 'transparent',
        borderRadius: '6px',
        cursor: 'pointer',
        fontWeight: 600,
        fontSize: '0.875rem',
        boxShadow: active ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        color: active ? 'var(--color-text)' : 'var(--color-text-tertiary)',
        transition: 'all 0.2s',
    } as React.CSSProperties)

    return (
        <div className="auth-layout">
            <div className="auth-grid" />
            <div className="auth-orb auth-orb--1" />
            <div className="auth-orb auth-orb--2" />
            <div className="auth-orb auth-orb--3" />
            <div className="auth-orb auth-orb--4" />
            <div className="auth-orb auth-orb--5" />
            <div className="auth-container">
                <div className="auth-logo" style={{ marginBottom: '1.5rem' }}>
                    <img
                        src="/rentnova-logo.png"
                        alt="RentNova — Property Management Tools"
                        style={{ width: 200, height: 'auto', objectFit: 'contain', display: 'block' }}
                    />
                </div>

                <div className="auth-card">
                    <h1 className="auth-title">Create your account</h1>
                    <p className="auth-subtitle">
                        {role === 'tenant'
                            ? 'Set up your tenant portal and connect your home.'
                            : 'Start managing your rentals for free.'}
                    </p>

                    {/* Role Toggle */}
                    <div style={{
                        display: 'flex',
                        gap: '0.25rem',
                        marginBottom: '1.75rem',
                        padding: '0.25rem',
                        background: 'var(--color-bg-secondary)',
                        borderRadius: '8px',
                    }}>
                        <button type="button" onClick={() => setRole('landlord')} style={toggleStyle(role === 'landlord')}>
                            <Building2 size={15} /> I'm a Landlord
                        </button>
                        <button type="button" onClick={() => setRole('tenant')} style={toggleStyle(role === 'tenant')}>
                            <User size={15} /> I'm a Tenant
                        </button>
                    </div>

                    <form className="auth-form" onSubmit={handleSubmit}>
                        {error && (
                            <div style={{
                                color: '#b91c1c',
                                backgroundColor: '#fee2e2',
                                padding: '0.75rem 1rem',
                                borderRadius: 'var(--radius-md)',
                                fontSize: '0.875rem',
                                marginBottom: '1rem',
                                lineHeight: 1.5,
                            }}>
                                {error}
                            </div>
                        )}

                        <div className="auth-field">
                            <label>Full Name</label>
                            <input
                                type="text"
                                placeholder="Your full name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                required
                                autoComplete="name"
                            />
                        </div>
                        <div className="auth-field">
                            <label>Email</label>
                            <input
                                type="email"
                                placeholder="you@example.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                autoComplete="email"
                            />
                        </div>
                        <div className="auth-field">
                            <label>Password</label>
                            <input
                                type="password"
                                placeholder="Min. 8 characters"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                minLength={8}
                                autoComplete="new-password"
                            />
                        </div>

                        {/* Invite code — only for tenants, optional */}
                        {role === 'tenant' && (
                            <div className="auth-field" style={{ marginTop: '0.25rem' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <KeyRound size={14} color="var(--color-accent)" />
                                    Invite Code
                                    <span style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)', fontWeight: 400 }}>
                                        — from your landlord
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="e.g. ABC123"
                                    value={inviteCode}
                                    onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                                    style={{
                                        fontFamily: 'monospace',
                                        letterSpacing: '0.12em',
                                        textTransform: 'uppercase',
                                        borderColor: inviteCode ? 'var(--color-accent)' : undefined,
                                    }}
                                    autoComplete="off"
                                />
                                <p style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)', marginTop: '0.25rem' }}>
                                    Optional — you can also enter this later from your dashboard.
                                </p>
                            </div>
                        )}

                        <button
                            type="submit"
                            className="btn btn-primary"
                            style={{ width: '100%', marginTop: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                            disabled={loading}
                        >
                            {loading ? (
                                <><Loader2 size={16} className="animate-spin" /> Creating account…</>
                            ) : role === 'tenant' ? (
                                'Create Tenant Account'
                            ) : (
                                'Create Landlord Account'
                            )}
                        </button>
                    </form>
                </div>

                <p className="auth-footer">
                    Already have an account? <Link to="/login">Log in</Link>
                </p>
            </div>
        </div>
    )
}
